import moment from 'moment';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Button, Pagination } from '@xsolla/ui-core';
import { ChevronDown, ChevronUp } from '@xsolla/ui-icons';

import { getUserLoginHistory } from '../../../../api/users';
import { UserLoginHistoryRecord } from '../../../../api/users/types';
import { RouteUserParams } from '../../../../routes/types';
import { getTranslationsData } from '../../../../utils';
import { Table } from '../../../ui';
import { LoginHistoryTableWrapper, NoDataContentBox, PaginationWrapper } from './style';

const ITEMS_PER_PAGE = 5;

const UserLoginHistory = ({ match }: RouteComponentProps): JSX.Element => {
  const intl = useIntl();
  const enTranslations = useMemo(() => getTranslationsData('en'), []);
  const { projectId, userId } = match.params as RouteUserParams;

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<UserLoginHistoryRecord[]>();
  const [page, setPage] = useState(1);

  const fetchLoginHistory = async () => {
    setIsLoading(true);
    try {
      const data = await getUserLoginHistory(projectId, userId);
      setHistory(data);
      setIsOpen(true);
    } catch (error) {
      toast.error(intl.formatMessage({ id: 'users.settings.general-info.data-fetching-error' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen && !history) {
      fetchLoginHistory();
    } else {
      setIsOpen((prev) => !prev);
    }
  };

  const pagedData = useMemo(() => {
    if (!history) return [];
    const start = (page - 1) * ITEMS_PER_PAGE;
    return history.slice(start, start + ITEMS_PER_PAGE);
  }, [history, page]);

  const dateHeader = useMemo(() => {
    const dateStr = intl.messages['users.settings.general-info.table.date'];
    const offset = moment().format('Z');
    return `${dateStr} (GMT${offset})`;
  }, [intl]);

  const tableHeaders = [
    {
      width: 30,
      label: <span style={{ paddingLeft: '12px' }}>{dateHeader}</span>,
      name: 'date'
    },
    {
      width: 23,
      label: intl.messages['users.settings.general-info.table.country'],
      name: 'country'
    },
    {
      width: 24,
      label: intl.messages['users.settings.general-info.table.city'],
      name: 'city'
    },
    {
      width: 23,
      label: intl.messages['users.settings.general-info.table.ip-address'],
      name: 'ipAddress',
      separator: false
    }
  ];

  const renderRow = ({ city, country, date, ip }: UserLoginHistoryRecord) => ({
    main: [
      <span style={{ paddingLeft: '12px' }}>{moment(date).format('DD MMM YYYY, h:mm a')}</span>,
      enTranslations[`form.select.country-name.${country}`] ?? country,
      city,
      ip
    ]
  });

  const noDataContent = (
    <NoDataContentBox>
      {intl.messages['users.settings.general-info.no-login-history']}
    </NoDataContentBox>
  );

  return (
    <>
      <Button
        type="button"
        look="link"
        palette="brand"
        size="md"
        onClick={handleToggle}
        disabled={isLoading}
        data-test-id="user-login-history-toggle">
        {
          intl.messages[
            isOpen
              ? 'users.settings.general-info.hide-login-history'
              : 'users.settings.general-info.show-login-history'
          ]
        }
        {isOpen ? <ChevronUp /> : <ChevronDown />}
      </Button>

      {(isOpen || isLoading) && (
        <LoginHistoryTableWrapper>
          <Table
            fetching={isLoading}
            noDataContent={noDataContent}
            head={tableHeaders}
            renderRow={renderRow}
            tableData={pagedData}
          />
          {history && history.length > ITEMS_PER_PAGE && (
            <PaginationWrapper>
              <Pagination
                current={page}
                total={history?.length || 0}
                perPage={ITEMS_PER_PAGE}
                onChange={(newPage) => setPage(newPage)}
              />
            </PaginationWrapper>
          )}
        </LoginHistoryTableWrapper>
      )}
    </>
  );
};

export default withRouter(UserLoginHistory);
